import { ElementRef, inject, signal } from '@angular/core';

import {
  AdvancedFilterModel,
  AgPromise,
  BooleanAdvancedFilterModelType,
  ICellEditorParams,
  IDoesFilterPassParams,
  IFilter,
  IFilterParams,
  ISimpleFilter,
  ScalarAdvancedFilterModelType,
  TextAdvancedFilterModelType,
} from 'ag-grid-enterprise';
import { Observable, ReplaySubject } from 'rxjs';

import { CamAbstractInput, InputBase } from '@camelot/form-model';
import { CamBaseComponent } from '@camelot/utils';

import { ActiveFilterTag } from '../../../filters/types';
import { GridMetaData } from '../../services/dto/metadata';
import { CamGridData, GridContext } from '../grid-data';
import { FilterModelSource, IGridColDef } from '../types';

export const operatorMap: { [key: string]: string } = {
  contains: '%',
  equals: '=',
  greaterThan: '>',
  greaterThanOrEqual: '>=',
  lessThan: '<',
  lessThanOrEqual: '<=',
  notEqual: '!=',
};
export interface IFilterOptions {
  allow: boolean;
}
export interface IColOptions {
  cellRenderer?: any;
  dataSearch$?: (search: string, colId: string) => Observable<string[]>;
  editable?: boolean | ((params: any) => boolean);
  enumInfo?: {
    cellRenderer: any;
    enum: any | null;
    values: {
      id: string;
      name: string;
    }[];
  };
  filters?: IFilterOptions;
  highlight?: boolean;
  sortable?: boolean;
}
export interface IBaseCol {
  scope: string;
  col: GridMetaData;
  options: IColOptions;
}
export interface IInputOptions {
  fromView?: boolean;
  fromEdit?: boolean;
  value?: unknown;
}
export class BaseCol<T extends AdvancedFilterModel> {
  public data: IBaseCol;
  public model: CamGridData<any>;

  get filterInstance(): T | null {
    return this.model.api?.getFilterModel()[this.key] ?? null;
  }
  get key() {
    return this.data.col.name;
  }
  get options() {
    return this.data.options;
  }

  get enabledInFilter() {
    return this.data.options.filters?.allow ?? true;
  }

  get enabledInHighlight() {
    return this.data.options.highlight ?? this.data.col.highlight ?? false;
  }

  get inputLabel() {
    return `app.grid.${this.data.scope}.core.${this.data.col.name}`;
  }
  constructor(data: IBaseCol, model: CamGridData<any>) {
    this.data = data;
    this.model = model;
  }

  public getColDef(): IGridColDef {
    return {
      cellRenderer: this.options.cellRenderer ?? null,
      editable: this.options.editable ?? true,
      field: this.key,
      filterParams: {
        popup: false, // Force l'affichage du filtre dans le menu (pas en tant que popup)
      },
      headerName: this.inputLabel,
      hide: !this.data.col.shownByDefault,
      menuTabs: this.options.filters?.allow === false ? [] : ['filterMenuTab'],
      metaData: this.data.col,
      sortable: this.data.col.isSortable === false ? false : this.options.sortable,
    };
  }

  public getInputForm(_options?: IInputOptions): CamAbstractInput | null {
    return null;
  }

  public getFiltersModel(values: any): T | null {
    return null;
  }

  protected _extractFiltersByFilterType(
    filter: AdvancedFilterModel | null,
    allowedFilterTypes: string[]
  ): AdvancedFilterModel[] {
    if (!filter) {
      return [];
    }
    const extract = (filter: AdvancedFilterModel): AdvancedFilterModel[] => {
      if (filter.filterType === 'join') {
        return filter.conditions.flatMap(extract);
      }
      return allowedFilterTypes.includes(filter.type) ? [filter] : [];
    };

    return extract(filter);
  }
}

export class BaseFilterComponent<T extends AdvancedFilterModel, U extends BaseCol<T>> implements IFilter {
  public input: CamAbstractInput | null = null;

  protected _col: U | null = null;
  protected _model: T | null = null;
  protected _params: IFilterParams<unknown, GridContext> | null = null;
  protected _suppressApply = false;

  public filterTitle = signal('');
  public focus$ = new ReplaySubject<void>(1);

  public readonly host: HTMLElement = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  agInit(params: IFilterParams) {
    this._params = params;
    this._col = params.context.cols[params.column.getColId()] ?? null;
    this.filterTitle.set(this._col?.inputLabel ?? '');

    this.setInput();
  }

  isFilterActive() {
    // Never recreate the input here: `cam-input-choices` does not replay its ngOnInit on an input
    // change, so its form control and `changeValue$` subscription would be lost.
    return !!this._model;
  }

  // Resync the displayed value with the restored model without recreating the input; `_suppressApply`
  // blocks the `changeValue$` → apply chain that would otherwise re-fetch and drop the active view.
  afterGuiAttached() {
    this.focus$.next();
    const input = this.input;
    if (!(input instanceof InputBase) || !this._col) {
      return;
    }
    const source = this._col.getInputForm({ fromView: true });
    this._suppressApply = true;
    input.value = source instanceof InputBase ? source.value : null;
    this._suppressApply = false;
  }
  getModel() {
    return this._model;
  }
  setModel(model: any): void | AgPromise<void> {
    this._model = model;
  }
  refresh?(newParams: IFilterParams): boolean {
    return true;
  }
  doesFilterPass(params: IDoesFilterPassParams): boolean {
    if (!this._col?.key) {
      return true;
    }
    const data = params.data[this._col?.key];
    const model = this._model;
    if (data === undefined || data === null || !model) {
      return true;
    }
    if (Array.isArray(data)) {
      return data.some(item => this._validateDataAgainstModel(item, model));
    }
    return this._validateDataAgainstModel(data, model);
  }

  getTags(): ActiveFilterTag[] {
    return [];
  }

  getModelAsString(model: ISimpleFilter): string {
    return '';
  }

  apply(data: any) {
    if (this._suppressApply) {
      return;
    }
    this.setModel(this._col?.getFiltersModel(data));

    this._params?.api.onFilterChanged();
    this._params?.context.grid.filters.applyFilterModel({}, FilterModelSource.storage);
  }

  setInput() {
    this.input = this._col?.getInputForm({ fromView: true }) ?? null;
  }

  applyInputValue(value: any) {
    const data: any = [];
    data[this._col?.key ?? ''] = value;

    this.apply(data);
  }

  private _validateDataAgainstModel(data: any, model: AdvancedFilterModel): boolean {
    if (model.filterType === 'join') {
      if (!Array.isArray(model.conditions)) {
        return false;
      }

      if (model.type === 'AND') {
        return model.conditions.every(condition => this._validateDataAgainstModel(data, condition));
      } else if (model.type === 'OR') {
        return model.conditions.some(condition => this._validateDataAgainstModel(data, condition));
      }
    }

    if (['text', 'number', 'date', 'dateString', 'boolean'].includes(model.filterType)) {
      switch (model.filterType) {
        case 'text':
          return this._validateTextCondition(data, model.type, model.filter);
        case 'number':
          return this._validateScalarCondition(data, model.type, model.filter);
        case 'date':
        case 'dateString':
          return this._validateScalarCondition(
            new Date(data).getTime(),
            model.type,
            model.filter ? new Date(model.filter).getTime() : undefined
          );
        case 'boolean':
          return this._validateBooleanCondition(data, model.type);
        default:
          return false;
      }
    }

    return false;
  }

  private _validateTextCondition(data: string, type: TextAdvancedFilterModelType, filter?: string): boolean {
    switch (type) {
      case 'equals':
        return data === filter;
      case 'notEqual':
        return data !== filter;
      case 'contains':
        return filter ? data.includes(filter) : false;
      case 'notContains':
        return filter ? !data.includes(filter) : true;
      case 'startsWith':
        return filter ? data.startsWith(filter) : false;
      case 'endsWith':
        return filter ? data.endsWith(filter) : false;
      case 'blank':
        return data === '';
      case 'notBlank':
        return data !== '';
      default:
        return false;
    }
  }

  private _validateScalarCondition(
    data: number,
    type: ScalarAdvancedFilterModelType | 'contains' | 'notContains',
    filter?: number
  ): boolean {
    switch (type) {
      case 'equals':
      case 'contains':
        return data === filter;
      case 'notEqual':
      case 'notContains':
        return data !== filter;
      case 'lessThan':
        return filter !== undefined && data < filter;
      case 'lessThanOrEqual':
        return filter !== undefined && data <= filter;
      case 'greaterThan':
        return filter !== undefined && data > filter;
      case 'greaterThanOrEqual':
        return filter !== undefined && data >= filter;
      case 'blank':
        return data === null || data === undefined;
      case 'notBlank':
        return data !== null && data !== undefined;
      default:
        return false;
    }
  }

  private _validateBooleanCondition(data: boolean, type: BooleanAdvancedFilterModelType): boolean {
    return (type === 'true' && data === true) || (type === 'false' && data === false);
  }
}

export abstract class BaseEditorComponent<
  T extends AdvancedFilterModel = AdvancedFilterModel,
  U extends BaseCol<T> = BaseCol<T>,
  V = unknown,
> extends CamBaseComponent {
  public params!: ICellEditorParams<any, T, GridContext>;
  public value: T | null = null;

  public focus$ = new ReplaySubject<void>();
  public input: InputBase<any> | null = null;

  protected _col: U | null = null;

  agInit(params: ICellEditorParams) {
    this.params = params;
    this._col = params.context.cols[params.column.getColId()] ?? null;

    this.setInput();
  }

  abstract getValue(): V | null;

  afterGuiAttached() {
    this.focus$.next();
  }

  isPopup(): boolean {
    return false;
  }

  apply(_data: any) {
    // No-op for editors — AG Grid retrieves the value via getValue()
  }

  setInput() {
    const charPress = this.params.eventKey?.length === 1 ? this.params.eventKey : null;
    const value = charPress ?? this.params.value;
    const input = this._col?.getInputForm({ fromEdit: true, value }) ?? null;
    this.input = input instanceof InputBase ? input : null;
  }

  applyInputValue(value: any) {
    const data: any = [];
    data[this._col?.key ?? ''] = value;

    this.apply(data);
  }
}
